import { useEffect, useState, useCallback } from "react";
import { useTranslation } from "react-i18next";
import api from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import PageHeader from "../../components/PageHeader";
import { Button } from "../../components/ui/button";
import { toast } from "sonner";
import { MagnifyingGlass, Buildings, Export, GraduationCap, CalendarBlank, ShieldCheck, Stack, Plus, Trash, CaretRight } from "@phosphor-icons/react";

const RISK_CLS = { low: "bg-emerald-50 text-emerald-700", medium: "bg-amber-50 text-amber-700", high: "bg-rose-50 text-rose-700" };
const LIFECYCLE = ["prospect", "applicant", "admitted", "active", "graduated", "alumni", "dropped"];
const STATUS_COLOR = { prospect: "bg-secondary", applicant: "bg-indigo-50 text-indigo-700", admitted: "bg-cyan-50 text-cyan-700", active: "bg-emerald-50 text-emerald-700", graduated: "bg-purple-50 text-purple-700", alumni: "bg-blue-50 text-blue-700", dropped: "bg-rose-50 text-rose-700" };

export default function SIS() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [tab, setTab] = useState("students");
  const [students, setStudents] = useState([]);
  const [courses, setCourses] = useState([]);
  const [programs, setPrograms] = useState([]);
  const [enrollments, setEnrollments] = useState([]);
  const [calendar, setCalendar] = useState([]);
  const [audit, setAudit] = useState([]);
  const [q, setQ] = useState("");
  const [selected, setSelected] = useState(null);
  const [lifecycleEvents, setLifecycleEvents] = useState([]);
  const [progForm, setProgForm] = useState({ code: "", name: "", total_credits: 160, duration_years: 4 });
  const [enrForm, setEnrForm] = useState({ student_id: "", course_id: "", section: "A", semester: 1 });
  const [calForm, setCalForm] = useState({ title: "", type: "exam", start: "", end: "", program: "" });

  const load = useCallback(async () => {
    const [s, c, p, e, cal] = await Promise.all([
      api.get(`/students?institution_type=${user?.institution_type || "higher_ed"}`),
      api.get("/courses"), api.get("/programs"), api.get("/enrollments"), api.get("/calendar"),
    ]);
    setStudents(s.data); setCourses(c.data); setPrograms(p.data); setEnrollments(e.data); setCalendar(cal.data);
  }, [user?.institution_type]);
  useEffect(() => { load(); }, [load]);

  const loadAudit = useCallback(async () => {
    try { const { data } = await api.get("/audit?limit=100"); setAudit(data); }
    catch { setAudit([]); }
  }, []);
  useEffect(() => { if (tab === "audit") loadAudit(); }, [tab, loadAudit]);

  const openStudent = async (s) => {
    setSelected(s);
    if (s.student_id?.startsWith("stu_")) {
      try { const { data } = await api.get(`/students/${s.student_id}/lifecycle`); setLifecycleEvents(data); }
      catch { setLifecycleEvents([]); }
    } else { setLifecycleEvents([]); }
  };

  const updateStatus = async (newStatus) => {
    if (!selected) return;
    try {
      await api.patch(`/students/${selected.student_id}/status`, { status: newStatus, reason: "Updated via SIS" });
      toast.success(`Status → ${newStatus}`);
      const { data } = await api.get(`/students/${selected.student_id}/lifecycle`);
      setLifecycleEvents(data);
      setSelected(s => ({ ...s, status: newStatus }));
      load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Failed"); }
  };

  const saveProgram = async () => {
    if (!progForm.code || !progForm.name) return toast.error("Code and name required");
    try { await api.post("/programs", { ...progForm, total_credits: Number(progForm.total_credits), duration_years: Number(progForm.duration_years), pos: [] });
      toast.success("Program saved"); setProgForm({ code: "", name: "", total_credits: 160, duration_years: 4 }); load();
    } catch { toast.error("Failed"); }
  };

  const enrol = async () => {
    if (!enrForm.student_id || !enrForm.course_id) return toast.error("Student and course required");
    try { await api.post("/enrollments", { ...enrForm, semester: Number(enrForm.semester) });
      toast.success("Enrolled"); setEnrForm({ student_id: "", course_id: "", section: "A", semester: 1 }); load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Enrollment failed"); }
  };

  const dropEnr = async (id) => {
    if (!window.confirm("Drop this enrollment?")) return;
    try { await api.delete(`/enrollments/${id}`); toast.success("Dropped"); load(); } catch { toast.error("Failed"); }
  };

  const addCal = async () => {
    if (!calForm.title || !calForm.start) return toast.error("Title and start required");
    try { await api.post("/calendar", { ...calForm, end: calForm.end || null, program: calForm.program || null });
      toast.success("Event added"); setCalForm({ title: "", type: "exam", start: "", end: "", program: "" }); load();
    } catch { toast.error("Failed"); }
  };

  const delCal = async (id) => {
    try { await api.delete(`/calendar/${id}`); toast.success("Deleted"); load(); } catch { toast.error("Failed"); }
  };

  const filtered = students.filter(s => s.name.toLowerCase().includes(q.toLowerCase()) || s.student_id.toLowerCase().includes(q.toLowerCase()));

  const tabs = [
    { v: "students", l: "Student Master", I: Buildings },
    { v: "programs", l: "Programs", I: GraduationCap },
    { v: "enrollments", l: "Enrollments", I: Stack },
    { v: "calendar", l: "Academic Calendar", I: CalendarBlank },
    { v: "audit", l: "Audit Log", I: ShieldCheck, adminOnly: true },
  ].filter(it => !it.adminOnly || user?.role === "admin");

  return (
    <div>
      <PageHeader
        overline="M05 · Academic Spine"
        title={t("modules.sis.title")}
        description={t("modules.sis.subtitle")}
        action={<Button variant="outline" className="rounded-md" data-testid="export-students-btn"><Export size={14} className="mr-1.5"/> Export</Button>}
      />
      <div className="p-6 lg:p-8 space-y-6">
        <div className="inline-flex flex-wrap border border-border rounded-md overflow-hidden" data-testid="sis-tabs">
          {tabs.map(it => (
            <button key={it.v} onClick={() => setTab(it.v)}
              className={`px-4 py-2 text-sm inline-flex items-center gap-1.5 border-r border-border last:border-r-0 ${tab === it.v ? "bg-[hsl(244_53%_20%)] text-white" : "bg-white hover:bg-secondary"}`}
              data-testid={`sis-tab-${it.v}`}>
              <it.I size={14}/> {it.l}
            </button>
          ))}
        </div>

        {tab === "students" && (
          <>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-px bg-border border border-border rounded-md overflow-hidden">
            {[
              { l: "Total Students", v: students.length, ti: user?.institution_type === "k12" ? "K-12" : "Higher Ed" },
              { l: "Active Programs", v: programs.length || (user?.institution_type === "k12" ? 7 : 28), ti: "NEP-aligned credits" },
              { l: "Faculty", v: user?.institution_type === "k12" ? 84 : 312, ti: "FTE" },
              { l: "Departments", v: user?.institution_type === "k12" ? 6 : 14, ti: "with HoDs" },
            ].map(s => (
              <div key={s.l} className="bg-white p-5">
                <div className="eos-overline">{s.l}</div>
                <div className="font-heading font-extrabold text-3xl mt-2 tracking-tight">{s.v}</div>
                <div className="text-[11px] text-muted-foreground mt-1">{s.ti}</div>
              </div>
            ))}
          </div>

          <div className="eos-card p-0 overflow-hidden">
            <div className="p-4 border-b border-border flex items-center justify-between gap-3">
              <div className="flex items-center gap-2">
                <Buildings size={16} weight="duotone"/>
                <div className="font-heading font-semibold text-base">Student master records</div>
              </div>
              <div className="flex items-center gap-2 px-3 py-1.5 border border-border rounded-md bg-secondary">
                <MagnifyingGlass size={14} className="text-muted-foreground"/>
                <input value={q} onChange={e => setQ(e.target.value)} placeholder="Search name or ID…" className="bg-transparent text-sm outline-none w-56" data-testid="student-search"/>
              </div>
            </div>
            <table className="w-full text-sm">
              <thead className="bg-secondary text-muted-foreground">
                <tr>{["ID", "Name", "Program", "Status", "APAAR", "CGPA", "Att%", ""].map(h => (
                  <th key={h} className="text-left px-4 py-2.5 text-[11px] uppercase tracking-[0.15em] font-medium">{h}</th>
                ))}</tr>
              </thead>
              <tbody>
                {filtered.map(s => (
                  <tr key={s.student_id} onClick={() => openStudent(s)} className="border-t border-border hover:bg-secondary/40 cursor-pointer" data-testid={`student-row-${s.student_id}`}>
                    <td className="px-4 py-3 font-mono-eos text-[12px]">{s.student_id}</td>
                    <td className="px-4 py-3 font-medium">{s.name}</td>
                    <td className="px-4 py-3 text-muted-foreground">{s.program}{s.year ? ` · Y${s.year}` : ""}</td>
                    <td className="px-4 py-3"><span className={`text-[10px] px-2 py-1 rounded-md ${STATUS_COLOR[s.status] || "bg-secondary text-muted-foreground"}`}>{s.status || "active"}</span></td>
                    <td className="px-4 py-3 font-mono-eos text-[11px]">{s.apaar_id || "—"}</td>
                    <td className="px-4 py-3 font-mono-eos">{s.cgpa || "—"}</td>
                    <td className="px-4 py-3">{s.attendance ? `${s.attendance}%` : "—"}</td>
                    <td className="px-4 py-3"><CaretRight size={12}/></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          </>
        )}

        {tab === "programs" && (
          <div className="grid lg:grid-cols-2 gap-4" data-testid="programs-pane">
            <div className="eos-card">
              <div className="font-heading font-bold text-base mb-3">Programs ({programs.length})</div>
              <div className="space-y-2 max-h-96 overflow-y-auto">
                {programs.length === 0 && <div className="text-sm text-muted-foreground italic">No programs configured.</div>}
                {programs.map(p => (
                  <div key={p.code} className="p-3 border border-border rounded-md" data-testid={`program-${p.code}`}>
                    <div className="flex items-center justify-between">
                      <div>
                        <div className="font-mono-eos text-[11px] text-muted-foreground">{p.code}</div>
                        <div className="font-medium text-sm">{p.name}</div>
                      </div>
                      <div className="text-right text-[11px] text-muted-foreground">
                        <div>{p.total_credits} credits</div>
                        <div>{p.duration_years} yrs</div>
                      </div>
                    </div>
                    {p.pos?.length > 0 && (
                      <div className="mt-2 flex flex-wrap gap-1">
                        {p.pos.map((po, i) => <span key={i} className="text-[10px] bg-emerald-50 text-emerald-700 px-1.5 py-0.5 rounded">{po.code}</span>)}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
            <div className="eos-card">
              <div className="font-heading font-bold text-base mb-3">Add / update program</div>
              <div className="space-y-2">
                {[["code", "Code (e.g. BTECH-CSE)"], ["name", "Name"], ["total_credits", "Total credits"], ["duration_years", "Duration (years)"]].map(([k, l]) => (
                  <div key={k}>
                    <label className="eos-overline">{l}</label>
                    <input value={progForm[k]} onChange={(e) => setProgForm(f => ({ ...f, [k]: e.target.value }))} className="mt-1 w-full px-2 py-2 text-sm border border-border rounded-md" data-testid={`prog-input-${k}`}/>
                  </div>
                ))}
                <Button onClick={saveProgram} className="w-full bg-[hsl(244_53%_20%)] hover:bg-[hsl(244_53%_15%)] mt-2" data-testid="save-program-btn">
                  <Plus size={14} className="mr-1.5"/> Save program
                </Button>
              </div>
            </div>
          </div>
        )}

        {tab === "enrollments" && (
          <div className="space-y-4" data-testid="enrollments-pane">
            <div className="eos-card">
              <div className="font-heading font-bold text-base mb-3">New enrollment</div>
              <div className="text-[11px] text-muted-foreground mb-3">Validates credit cap (28/sem), section capacity (60), prerequisites.</div>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                {[["student_id", "Student ID"], ["course_id", "Course ID"], ["section", "Section"], ["semester", "Semester"]].map(([k, l]) => (
                  <div key={k}>
                    <label className="eos-overline">{l}</label>
                    <input value={enrForm[k]} onChange={(e) => setEnrForm(f => ({ ...f, [k]: e.target.value }))} className="mt-1 w-full px-2 py-2 text-sm border border-border rounded-md" data-testid={`enr-input-${k}`}/>
                  </div>
                ))}
              </div>
              <Button onClick={enrol} className="mt-3 bg-[hsl(20_90%_48%)] hover:bg-[hsl(20_90%_42%)]" data-testid="enrol-btn">Enroll</Button>
            </div>
            <div className="eos-card p-0 overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-secondary text-muted-foreground">
                  <tr>{["Student", "Course", "Section", "Sem", "Status", "Enrolled", ""].map(h => (
                    <th key={h} className="text-left px-4 py-2.5 text-[11px] uppercase tracking-[0.15em] font-medium">{h}</th>
                  ))}</tr>
                </thead>
                <tbody>
                  {enrollments.map(e => (
                    <tr key={e.enrollment_id} className="border-t border-border" data-testid={`enr-${e.enrollment_id}`}>
                      <td className="px-4 py-3 font-mono-eos text-[12px]">{e.student_id}</td>
                      <td className="px-4 py-3 font-mono-eos text-[12px]">{e.course_id}</td>
                      <td className="px-4 py-3 font-mono-eos">{e.section}</td>
                      <td className="px-4 py-3 font-mono-eos">{e.semester}</td>
                      <td className="px-4 py-3"><span className={`text-[10px] px-2 py-1 rounded ${e.status === "active" ? "bg-emerald-50 text-emerald-700" : "bg-rose-50 text-rose-700"}`}>{e.status}</span></td>
                      <td className="px-4 py-3 text-[11px] text-muted-foreground">{new Date(e.enrolled_at).toLocaleDateString()}</td>
                      <td className="px-4 py-3">
                        {e.status === "active" && <Button variant="ghost" onClick={() => dropEnr(e.enrollment_id)} className="h-6 px-2 text-[10px] text-rose-600" data-testid={`drop-${e.enrollment_id}`}>Drop</Button>}
                      </td>
                    </tr>
                  ))}
                  {enrollments.length === 0 && <tr><td colSpan="7" className="p-10 text-center text-sm text-muted-foreground">No enrollments yet.</td></tr>}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {tab === "calendar" && (
          <div className="space-y-4" data-testid="calendar-pane">
            <div className="eos-card">
              <div className="font-heading font-bold text-base mb-3">Add academic calendar event</div>
              <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
                <div>
                  <label className="eos-overline">Title</label>
                  <input value={calForm.title} onChange={e => setCalForm(f => ({ ...f, title: e.target.value }))} className="mt-1 w-full px-2 py-2 text-sm border border-border rounded-md" data-testid="cal-title"/>
                </div>
                <div>
                  <label className="eos-overline">Type</label>
                  <select value={calForm.type} onChange={e => setCalForm(f => ({ ...f, type: e.target.value }))} className="mt-1 w-full px-2 py-2 text-sm border border-border rounded-md bg-white" data-testid="cal-type">
                    {["semester_start", "semester_end", "exam", "holiday", "registration", "other"].map(t => <option key={t} value={t}>{t.replace(/_/g, " ")}</option>)}
                  </select>
                </div>
                <div>
                  <label className="eos-overline">Start</label>
                  <input type="date" value={calForm.start} onChange={e => setCalForm(f => ({ ...f, start: e.target.value }))} className="mt-1 w-full px-2 py-2 text-sm border border-border rounded-md" data-testid="cal-start"/>
                </div>
                <div>
                  <label className="eos-overline">End (optional)</label>
                  <input type="date" value={calForm.end} onChange={e => setCalForm(f => ({ ...f, end: e.target.value }))} className="mt-1 w-full px-2 py-2 text-sm border border-border rounded-md" data-testid="cal-end"/>
                </div>
                <div>
                  <label className="eos-overline">Program (opt)</label>
                  <input value={calForm.program} onChange={e => setCalForm(f => ({ ...f, program: e.target.value }))} className="mt-1 w-full px-2 py-2 text-sm border border-border rounded-md" data-testid="cal-program"/>
                </div>
              </div>
              <Button onClick={addCal} className="mt-3 bg-[hsl(244_53%_20%)] hover:bg-[hsl(244_53%_15%)]" data-testid="save-cal-btn"><Plus size={14} className="mr-1.5"/> Add event</Button>
            </div>
            <div className="eos-card p-0 overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-secondary text-muted-foreground">
                  <tr>{["Date", "Title", "Type", "Program", ""].map(h => <th key={h} className="text-left px-4 py-2.5 text-[11px] uppercase tracking-[0.15em] font-medium">{h}</th>)}</tr>
                </thead>
                <tbody>
                  {calendar.map(c => (
                    <tr key={c.event_id} className="border-t border-border" data-testid={`cal-${c.event_id}`}>
                      <td className="px-4 py-3 font-mono-eos">{c.start}{c.end && ` → ${c.end}`}</td>
                      <td className="px-4 py-3 font-medium">{c.title}</td>
                      <td className="px-4 py-3"><span className="text-[10px] bg-indigo-50 text-indigo-700 px-1.5 py-0.5 rounded">{c.type.replace(/_/g, " ")}</span></td>
                      <td className="px-4 py-3 text-muted-foreground">{c.program || "—"}</td>
                      <td className="px-4 py-3"><Button variant="ghost" onClick={() => delCal(c.event_id)} className="h-6 w-6 p-0 text-rose-600" data-testid={`del-cal-${c.event_id}`}><Trash size={12}/></Button></td>
                    </tr>
                  ))}
                  {calendar.length === 0 && <tr><td colSpan="5" className="p-10 text-center text-sm text-muted-foreground">No events.</td></tr>}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {tab === "audit" && (
          <div className="eos-card p-0 overflow-hidden" data-testid="audit-pane">
            <div className="p-4 border-b border-border flex items-center gap-2">
              <ShieldCheck size={16} weight="duotone"/>
              <div className="font-heading font-semibold text-base">Immutable audit log · last 100</div>
              <Button variant="outline" onClick={loadAudit} className="ml-auto h-7 px-2 text-[11px]" data-testid="refresh-audit-btn">Refresh</Button>
            </div>
            <table className="w-full text-sm">
              <thead className="bg-secondary text-muted-foreground">
                <tr>{["At", "Actor", "Role", "Action", "Record", ""].map(h => <th key={h} className="text-left px-4 py-2.5 text-[11px] uppercase tracking-[0.15em] font-medium">{h}</th>)}</tr>
              </thead>
              <tbody>
                {audit.length === 0 && <tr><td colSpan="6" className="p-10 text-center text-sm text-muted-foreground">No audit events. Try creating a program / enrollment to populate.</td></tr>}
                {audit.map(a => (
                  <tr key={a.audit_id} className="border-t border-border" data-testid={`audit-${a.audit_id}`}>
                    <td className="px-4 py-3 text-[11px] text-muted-foreground font-mono-eos">{new Date(a.at).toLocaleString()}</td>
                    <td className="px-4 py-3 text-[12px]">{a.actor_email}</td>
                    <td className="px-4 py-3"><span className="text-[10px] bg-secondary px-1.5 py-0.5 rounded">{a.actor_role}</span></td>
                    <td className="px-4 py-3 font-mono-eos text-[12px]">{a.action}</td>
                    <td className="px-4 py-3 font-mono-eos text-[11px] text-muted-foreground">{a.record_id}</td>
                    <td className="px-4 py-3 text-[10px] text-muted-foreground max-w-xs truncate">{JSON.stringify(a.data || {}).slice(0, 80)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Student lifecycle drawer */}
      {selected && (
        <div className="fixed inset-0 z-50 flex" data-testid="student-drawer">
          <div className="flex-1 bg-black/40" onClick={() => setSelected(null)}/>
          <div className="w-full max-w-md bg-white shadow-xl border-l border-border overflow-y-auto">
            <div className="p-5 border-b border-border flex items-center justify-between">
              <div>
                <div className="eos-overline">Student master</div>
                <div className="font-heading font-bold text-lg mt-0.5">{selected.name}</div>
                <div className="text-[11px] text-muted-foreground font-mono-eos">{selected.student_id} · {selected.program}</div>
              </div>
              <button onClick={() => setSelected(null)} className="text-muted-foreground hover:text-foreground" data-testid="close-student-drawer">✕</button>
            </div>
            <div className="p-5 space-y-4">
              <div>
                <div className="eos-overline mb-2">Lifecycle status</div>
                <div className="flex flex-wrap gap-1.5">
                  {LIFECYCLE.map(st => (
                    <button key={st} onClick={() => updateStatus(st)} className={`text-[11px] px-2.5 py-1 rounded-md border ${selected.status === st ? "bg-[hsl(244_53%_20%)] text-white border-[hsl(244_53%_20%)]" : "border-border hover:bg-secondary"}`} data-testid={`status-${st}`}>
                      {st}
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <div className="eos-overline mb-2">Lifecycle history</div>
                {lifecycleEvents.length === 0 && <div className="text-sm text-muted-foreground italic">No transitions yet.</div>}
                <div className="space-y-2">
                  {lifecycleEvents.map(ev => (
                    <div key={ev.event_id} className="border-l-2 border-[hsl(244_53%_20%)] pl-3 py-1 text-[12px]" data-testid={`lc-${ev.event_id}`}>
                      <div className="text-muted-foreground text-[11px]">{new Date(ev.at).toLocaleString()} · {ev.actor}</div>
                      <div className="mt-0.5"><span className="font-mono-eos">{ev.from}</span> → <span className="font-medium font-mono-eos">{ev.to}</span></div>
                      {ev.reason && <div className="text-muted-foreground italic mt-0.5">"{ev.reason}"</div>}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
